import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Wand2,
  BookOpen,
  HelpCircle,
  FileText,
  Clock,
  List,
  Lightbulb,
  Zap,
  BrainCircuit,
} from "lucide-react";
import { useSources } from "../context/SourcesContext";
import NotebookPage from "../views/notebook";
import styles from "../styles/studioPanel.module.css";

const GENERATE_ACTIONS = [
  {
    id: "study_guide",
    label: "Study Guide",
    icon: BookOpen,
    prompt:
      "Generate a comprehensive, well-structured study guide from the provided sources. Include key concepts, definitions, important facts, and learning objectives organized by topic.",
    color: "#818cf8",
  },
  {
    id: "faq",
    label: "FAQ",
    icon: HelpCircle,
    prompt:
      "Generate a Frequently Asked Questions (FAQ) document based on the provided sources. Include the 10–15 most important questions a student would ask, with clear and concise answers.",
    color: "#34d399",
  },
  {
    id: "briefing",
    label: "Briefing Doc",
    icon: FileText,
    prompt:
      "Create a concise executive briefing document from the provided sources. Include: a summary, key findings, main arguments, and notable conclusions.",
    color: "#60a5fa",
  },
  {
    id: "timeline",
    label: "Timeline",
    icon: Clock,
    prompt:
      "Extract and organize all events, dates, and chronological information from the provided sources into a clear timeline. Format each entry as [Date/Period]: [Event/Development].",
    color: "#fb923c",
  },
  {
    id: "outline",
    label: "Outline",
    icon: List,
    prompt:
      "Create a detailed hierarchical outline of the main topics and subtopics covered in the provided sources. Use numbered sections and subsections.",
    color: "#a78bfa",
  },
  {
    id: "key_concepts",
    label: "Key Concepts",
    icon: Lightbulb,
    prompt:
      "Identify and explain the most important concepts, terms, and ideas from the provided sources. For each concept, provide: the term, a clear definition, and why it matters.",
    color: "#fbbf24",
  },
];

const StudioPanel = ({
  collapsed,
  onToggleCollapse,
  onSendToChat,
  onGenerateKnowledgeGraph,
  onOpenLumiWithMessage,
  onQueueLumiDraft,
}) => {
  const { sources, hasSourcesLoaded } = useSources();
  const [activeTab, setActiveTab] = useState("studio");
  const [generating, setGenerating] = useState(null);

  const handleGenerate = (action) => {
    if (!onSendToChat) return;
    setGenerating(action.id);
    onSendToChat(action.prompt);
    setTimeout(() => setGenerating(null), 1200);
  };

  if (collapsed) {
    return (
      <aside className={`${styles.panel} ${styles.panelCollapsed}`}>
        <button
          className={styles.collapseToggle}
          onClick={onToggleCollapse}
          title="Expand Studio"
        >
          <ChevronLeft size={14} />
        </button>
        <div className={styles.collapsedLabel}>
          <span>Studio</span>
        </div>
      </aside>
    );
  }

  return (
    <aside className={styles.panel}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === "studio" ? styles.tabActive : ""}`}
            onClick={() => setActiveTab("studio")}
          >
            <Wand2 size={12} strokeWidth={2} />
            Studio
          </button>
          <button
            className={`${styles.tab} ${activeTab === "notes" ? styles.tabActive : ""}`}
            onClick={() => setActiveTab("notes")}
          >
            <BookOpen size={12} strokeWidth={2} />
            Notes
          </button>
        </div>
        <button
          className={styles.collapseToggle}
          onClick={onToggleCollapse}
          title="Collapse Studio"
        >
          <ChevronRight size={14} />
        </button>
      </div>

      <div className={styles.body}>
        <AnimatePresence mode="wait">
          {activeTab === "studio" && (
            <motion.div
              key="studio"
              className={styles.studioTab}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.12 }}
            >
              {/* Audio Overview placeholder */}
              <div className={styles.audioOverview}>
                <div className={styles.audioHeader}>
                  <Zap size={14} strokeWidth={2} className={styles.audioIcon} />
                  <span>Audio Overview</span>
                  <span className={styles.comingSoon}>Coming soon</span>
                </div>
                <p className={styles.audioDesc}>
                  Generate a podcast-style discussion of your sources.
                </p>
              </div>

              <div className={styles.divider} />

              {/* Generate section */}
              <div className={styles.generateSection}>
                <div className={styles.sectionHeader}>
                  <BrainCircuit size={13} strokeWidth={1.8} />
                  <span>Generate</span>
                </div>
                {!hasSourcesLoaded && (
                  <p className={styles.noSourcesHint}>
                    Add sources on the left to enable generation.
                  </p>
                )}
                <div className={styles.generateGrid}>
                  {GENERATE_ACTIONS.map((action) => {
                    const Icon = action.icon;
                    const isGen = generating === action.id;
                    return (
                      <motion.button
                        key={action.id}
                        className={`${styles.generateChip} ${!hasSourcesLoaded ? styles.generateChipDisabled : ""}`}
                        onClick={() => hasSourcesLoaded && handleGenerate(action)}
                        whileHover={hasSourcesLoaded ? { scale: 1.02 } : {}}
                        whileTap={hasSourcesLoaded ? { scale: 0.97 } : {}}
                        title={hasSourcesLoaded ? action.label : "Add sources first"}
                      >
                        <span
                          className={styles.chipIcon}
                          style={{ color: action.color }}
                        >
                          {isGen ? (
                            <motion.span
                              animate={{ rotate: 360 }}
                              transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
                              style={{ display: "inline-flex" }}
                            >
                              <Wand2 size={12} />
                            </motion.span>
                          ) : (
                            <Icon size={12} strokeWidth={2} />
                          )}
                        </span>
                        <span className={styles.chipLabel}>{action.label}</span>
                      </motion.button>
                    );
                  })}
                </div>
              </div>

              {/* Source summary */}
              {hasSourcesLoaded && (
                <div className={styles.sourcesSummary}>
                  <span className={styles.sourcesCount}>
                    {sources.filter((s) => !s.isProcessing && !s.processError).length} source
                    {sources.filter((s) => !s.isProcessing && !s.processError).length !== 1 ? "s" : ""} loaded
                  </span>
                  <span className={styles.sourcesReadyDot} />
                  <span className={styles.sourcesReadyLabel}>Ready</span>
                </div>
              )}
            </motion.div>
          )}

          {activeTab === "notes" && (
            <motion.div
              key="notes"
              className={styles.notesTab}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.12 }}
            >
              <NotebookPage
                onGenerateKnowledgeGraph={onGenerateKnowledgeGraph}
                onOpenLumiWithMessage={onOpenLumiWithMessage}
                onQueueLumiDraft={onQueueLumiDraft}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </aside>
  );
};

export default StudioPanel;
